<script setup lang="ts">
import SectionMainImage from "../assets/images/prices.webp";

const { t } = useI18n();

const getAccountingJDGTableHeaders = (index: number) =>
  t(`pricesPage.accountingServicesJDG.headerTitles[${index}]`);

const getAccountingJDGTariffColumnTitle = (index: number) =>
  t(`pricesPage.accountingServicesJDG.tariffColumnTitles[${index}]`);

const getAccountingCompanyTableHeaders = (index: number) =>
  t(`pricesPage.accountingServicesCompany.headerTitles[${index}]`);

const getAccountingCompanyTariffColumnTitle = (index: number) =>
  t(`pricesPage.accountingServicesCompany.tariffColumnTitles[${index}]`);

const getPayrollAndInsuranceServicesTableHeaders = (index: number) =>
  t(`pricesPage.payrollAndInsuranceServices.headerTitles[${index}]`);

const getPayrollAndInsuranceServicesEmployeesColumnTitle = (index: number) =>
  t(`pricesPage.payrollAndInsuranceServices.employeesColumnTitles[${index}]`);

const getPayrollAndInsuranceServicesPeriodColumnTitle = (index: number) =>
  t(`pricesPage.payrollAndInsuranceServices.periodColumnTitles[${index}]`);

const getAdditionalServicesPersonnelTableHeaders = (index: number) =>
  t(`pricesPage.additionalServicesPersonnel.headerTitles[${index}]`);

const getAdditionalServicesPersonnelServiceColumnTitle = (index: number) =>
  t(`pricesPage.additionalServicesPersonnel.serviceColumnTitles[${index}]`);

const getLegalizationOfStayServicesTableHeaders = (index: number) =>
  t(`pricesPage.legalizationOfStayServices.headerTitles[${index}]`);

const getLegalizationOfStayServicesServiceColumnTitle = (index: number) =>
  t(`pricesPage.legalizationOfStayServices.serviceColumnTitles[${index}]`);

const getProfessionalLegalServicesTableHeaders = (index: number) =>
  t(`pricesPage.professionalLegalServices.headerTitles[${index}]`);

const getProfessionalLegalServicesColumnTitle = (index: number) =>
  t(`pricesPage.professionalLegalServices.serviceColumnTitles[${index}]`);

useHead({
  meta: [
    {
      name: "robots",
      content: "index, follow",
    },
    {
      name: "title",
      content: t("pricesPage.title"),
    },
    {
      name: "og:title",
      content: t("pricesPage.title"),
    },
    {
      name: "description",
      content: t("pricesPage.description"),
    },
    {
      name: "og:description",
      content: t("pricesPage.description"),
    },
    {
      name: "og:type",
      content: "website",
    },
  ],
});
</script>
<template>
  <section id="prices__page" class="font-inter">
    <PageContainer>
      <GenericSectionMain
        :img-width="606"
        :img-height="333"
        :img-src="SectionMainImage"
        img-alt="Prices page illustration"
      >
        <template #title>
          {{ t("pricesPage.title") }}
        </template>
        <template #description>
          {{ t("pricesPage.description") }}
        </template>
      </GenericSectionMain>
    </PageContainer>
    <div class="gradient-background mb-20">
      <PageContainer>
        <div class="flex text-center py-16 text-xl font-sans">
          {{ t("pricesPage.text") }}
        </div>
      </PageContainer>
    </div>
    <PageContainer>
      <h2 class="mt-2 text-2xl">
        {{ t("pricesPage.accountingServicesJDG.title") }}
      </h2>
      <PricesTable
        class="w-full"
        :headers="[
          {
            name: 'rate',
            header: getAccountingJDGTableHeaders(0),
          },
          {
            name: 'number',
            header: getAccountingJDGTableHeaders(1),
          },
          {
            name: 'price',
            header: getAccountingJDGTableHeaders(2),
          },
          {
            name: 'tax',
            header: getAccountingJDGTableHeaders(3),
          },
        ]"
        :items="[
          {
            rate: getAccountingJDGTariffColumnTitle(0),
            number: '0-10',
            price: '290,00',
            tax: '350,00',
          },
          {
            rate: getAccountingJDGTariffColumnTitle(1),
            number: '11-30',
            price: '549,00',
            tax: '600,00',
          },
          {
            rate: getAccountingJDGTariffColumnTitle(2),
            number: '31-65',
            price: '790,00',
            tax: '850,00',
          },
          {
            rate: getAccountingJDGTariffColumnTitle(3),
            number: '66-100',
            price: '1049,00',
            tax: '1149,00',
          },
        ]"
      ></PricesTable>

      <h2 class="mt-8 text-2xl">
        {{ t("pricesPage.accountingServicesCompany.title") }}
      </h2>
      <PricesTable
        class="w-full"
        :headers="[
          {
            name: 'tariff',
            header: getAccountingCompanyTableHeaders(0),
          },
          {
            name: 'number',
            header: getAccountingCompanyTableHeaders(1),
          },
          {
            name: 'price',
            header: getAccountingCompanyTableHeaders(2),
          },
        ]"
        :items="[
          {
            tariff: getAccountingCompanyTariffColumnTitle(0),
            number: '0-10',
            price: '690,00',
          },
          {
            tariff: getAccountingCompanyTariffColumnTitle(1),
            number: '11-30',
            price: '949,00',
          },
          {
            tariff: getAccountingCompanyTariffColumnTitle(2),
            number: '31-50',
            price: '1590,00',
          },
          {
            tariff: getAccountingCompanyTariffColumnTitle(3),
            number: '51-80',
            price: '2249,00',
          },
          {
            tariff: getAccountingCompanyTariffColumnTitle(4),
            number: '81-100',
            price: '3190,00',
          },
        ]"
      ></PricesTable>

      <!-- <div class="text-sm text-right mt-5 w-full">
        <div class="w-44">+ obowiązujący podatek VAT applicable VAT</div>
      </div> -->
      <h2 class="mt-16 text-2xl text-center">
        {{ t("pricesPage.payrollAndInsuranceServices.title") }}
      </h2>
      <PricesTable
        class="w-full"
        :headers="[
          {
            name: 'number',
            header: getPayrollAndInsuranceServicesTableHeaders(0),
            isItemsTextLeft: true,
          },
          {
            name: 'period',
            header: getPayrollAndInsuranceServicesTableHeaders(1),
          },
          {
            name: 'amount',
            header: getPayrollAndInsuranceServicesTableHeaders(2),
          },
        ]"
        :items="[
          {
            number: getPayrollAndInsuranceServicesEmployeesColumnTitle(0),
            period: getPayrollAndInsuranceServicesPeriodColumnTitle(0),
            amount: '85,00',
          },
          {
            number: getPayrollAndInsuranceServicesEmployeesColumnTitle(1),
            period: getPayrollAndInsuranceServicesPeriodColumnTitle(1),
            amount: '75,00',
          },
          {
            number: getPayrollAndInsuranceServicesEmployeesColumnTitle(2),
            period: getPayrollAndInsuranceServicesPeriodColumnTitle(2),
            amount: '75,00',
          },
          {
            number: getPayrollAndInsuranceServicesEmployeesColumnTitle(3),
            period: getPayrollAndInsuranceServicesPeriodColumnTitle(3),
            amount: '90,00',
          },
          {
            number: getPayrollAndInsuranceServicesEmployeesColumnTitle(4),
            period: getPayrollAndInsuranceServicesPeriodColumnTitle(4),
            amount: '140,00',
          },
        ]"
      ></PricesTable>
      <div class="mt-20 text-xl <md:px-2">
        {{
          $t(
            "za-zamkniecie-roku-sporzadzenie-sprawozdania-finansowego-i-rozliczenie-podatkowe-przysluguje-oddzielne-wynagrodzenie-w-wysokosci-sredniomiesiecznego-wynagrodzenia-za-rok-obrotowy-ale-nie-mniej-niz-490-00-zl-for-the-end-of-the-year-preparation-of-financial-statements-and-tax-settlement-a-separate-fee-is-payable-in-the-amount-of-the-average-monthly-remuneration-for-the-financial-year-of-accounting-services-but-not-less-than-pln-490-00",
          )
        }}
      </div>
    </PageContainer>
    <div class="gradient-background py-10 mt-14">
      <PageContainer>
        <div class="text-xl <md:px-2">
          {{
            $t(
              "przez-zapisy-ksiegowe-rozumie-sie-zapisy-ksiegowe-definiowane-przez-ustawe-o-rachunkowosci-z-dnia-29-wrzesnia-1994-roku-z-pozn-zmianami-w-szczegolnosci-jeden-dokument-ksiegowy-to",
            )
          }}
        </div>
        <ul class="text-white/75 grid grid-cols-1 gap-12 mt-12">
          <li>
            {{
              $t(
                "faktura-rachunek-zakupu-lub-sprzedazy-dobowy-raport-fiskalny-itp-przy-czym-1-faktura-zakupu-importowa-to-faktycznie-3-dokumenty-ksiegowe-obejmuje-dokumenty-wewnetrzne",
              )
            }}
          </li>
          <li>
            {{
              $t(
                "polecenie-ksiegowania-naliczenie-amortyzacji-ewidencja-przebiegu-pojazdu",
              )
            }}
          </li>
          <li>
            {{ $t("ilosc-transakcji-na-wyciagu-bankowym-raporcie-kasowym") }}
          </li>
          <li>{{ $t("rozliczenie-roznic-kursowych") }}</li>
        </ul>
      </PageContainer>
    </div>
    <PageContainer>
      <div class="text-center mt-20 text-2xl">
        <h2 class="my-0">Usługi kadrowo – płacowe i ubezpieczeniowe.</h2>
        <div class="mt-2">HR, payroll and insurance services.</div>
      </div>
      <!-- <div class="w-full !text-left mt-19">
        Obsługa kadrowo - płacowa (cena za rozliczenie każdego pracownika/
        zleceniobiorcy) <br />
        HR and payroll services (price for the settlement of each employee /
        contractor)
      </div> -->
      <h2 class="text-2xl mt-16">
        {{ t("pricesPage.additionalServicesPersonnel.title") }}
      </h2>
      <PricesTable
        :headers="[
          {
            header: getAdditionalServicesPersonnelTableHeaders(0),
            name: 'service',
            isItemsTextLeft: true,
          },
          {
            header: getAdditionalServicesPersonnelTableHeaders(1),
            name: 'price',
          },
        ]"
        :items="[
          {
            service: getAdditionalServicesPersonnelServiceColumnTitle(0),
            price: 'Od 99,00',
          },
          {
            service: getAdditionalServicesPersonnelServiceColumnTitle(1),
            price: 'Od 250,00',
          },
          {
            service: getAdditionalServicesPersonnelServiceColumnTitle(2),
            price: '50,00',
          },
          {
            service: getAdditionalServicesPersonnelServiceColumnTitle(3),
            price: '50,00',
          },
          {
            service: getAdditionalServicesPersonnelServiceColumnTitle(4),
            price: '99,00',
          },
          {
            service: getAdditionalServicesPersonnelServiceColumnTitle(5),
            price: '290,00',
          },
          {
            service: getAdditionalServicesPersonnelServiceColumnTitle(6),
            price: '290,00',
          },
          {
            service: getAdditionalServicesPersonnelServiceColumnTitle(7),
            price: '50,00 osoba',
          },
          {
            service: getAdditionalServicesPersonnelServiceColumnTitle(8),
            price: '120,00',
          },
          {
            service: getAdditionalServicesPersonnelServiceColumnTitle(9),
            price: '50,00 osoba',
          },
          {
            service: getAdditionalServicesPersonnelServiceColumnTitle(10),
            price: '50,00',
          },
          {
            service: getAdditionalServicesPersonnelServiceColumnTitle(11),
            price: '250,00 ',
          },
          {
            service: getAdditionalServicesPersonnelServiceColumnTitle(12),
            price: '390,00 ',
          },
        ]"
      ></PricesTable>
      <h2 class="text-2xl mt-16 text-center">
        {{ t("pricesPage.legalizationOfStayServices.title") }}
      </h2>
      <PricesTable
        :headers="[
          {
            header: getLegalizationOfStayServicesTableHeaders(0),
            name: 'service',
            isItemsTextLeft: true,
          },
          {
            header: getLegalizationOfStayServicesTableHeaders(1),
            name: 'price',
          },
        ]"
        :items="[
          {
            service: getLegalizationOfStayServicesServiceColumnTitle(0),
            price: '',
          },
          {
            service: getLegalizationOfStayServicesServiceColumnTitle(1),
            price: '750,00',
          },
          {
            service: getLegalizationOfStayServicesServiceColumnTitle(2),
            price: '850,00',
          },
          {
            service: getLegalizationOfStayServicesServiceColumnTitle(3),
            price: '1450,00',
          },
          {
            service: getLegalizationOfStayServicesServiceColumnTitle(4),
            price: '550,00',
          },
          {
            service: getLegalizationOfStayServicesServiceColumnTitle(5),
            price: '550,00',
          },
          {
            service: getLegalizationOfStayServicesServiceColumnTitle(6),
            price: '450,00',
          },
          {
            service: getLegalizationOfStayServicesServiceColumnTitle(7),
            price: '',
          },
          {
            service: getLegalizationOfStayServicesServiceColumnTitle(8),
            price: '90,00',
          },
          {
            service: getLegalizationOfStayServicesServiceColumnTitle(9),
            price: '150,00',
          },
          {
            service: getLegalizationOfStayServicesServiceColumnTitle(10),
            price: '90,00',
          },
        ]"
      ></PricesTable>
      <!-- <div class="text-left w-full">Dodatkowe opłaty wynikające z umowy:</div> -->
      <h2 class="text-2xl mt-16">
        {{ t("pricesPage.professionalLegalServices.title") }}
      </h2>
      <PricesTable
        class="w-full"
        :headers="[
          {
            header: getProfessionalLegalServicesTableHeaders(0),
            name: 'service',
            isItemsTextLeft: true,
          },
          {
            header: getProfessionalLegalServicesTableHeaders(1),
            name: 'price',
          },
        ]"
        :items="[
          {
            service: getProfessionalLegalServicesColumnTitle(0),
            price: '',
          },
          {
            service: getProfessionalLegalServicesColumnTitle(1),
            price: '350,00',
          },
          {
            service: getProfessionalLegalServicesColumnTitle(2),
            price: 'Od 650,00',
          },
          {
            service: getProfessionalLegalServicesColumnTitle(3),
            price: 'Od 3250,00',
          },
          {
            service: getProfessionalLegalServicesColumnTitle(4),
            price: '450,00',
          },
          {
            service: getProfessionalLegalServicesColumnTitle(5),
            price: 'Od 5000,00',
          },
          {
            service: getProfessionalLegalServicesColumnTitle(6),
            price: 'Od 6000,00',
          },
          {
            service: getProfessionalLegalServicesColumnTitle(7),
            price: 'Od 5000,00',
          },
          {
            service: getProfessionalLegalServicesColumnTitle(8),
            price: 'Od 1500,00',
          },
          {
            service: getProfessionalLegalServicesColumnTitle(9),
            price: '',
          },
          {
            service: getProfessionalLegalServicesColumnTitle(10),
            price: 'Od 350,00 godzina',
          },
          {
            service: getProfessionalLegalServicesColumnTitle(11),
            price: '250,00',
          },
          {
            service: getProfessionalLegalServicesColumnTitle(12),
            price: 'Od 250,00',
          },
          {
            service: getProfessionalLegalServicesColumnTitle(13),
            price: 'Od 99,00',
          },
          {
            service: getProfessionalLegalServicesColumnTitle(14),
            price: 'Od 199,00',
          },
          {
            service: getProfessionalLegalServicesColumnTitle(15),
            price: 'Od 199,00',
          },
          {
            service: getProfessionalLegalServicesColumnTitle(16),
            price: 'Od 199,00',
          },
          {
            service: getProfessionalLegalServicesColumnTitle(17),
            price: '120,00',
          },
          {
            service: getProfessionalLegalServicesColumnTitle(18),
            price: '120,00',
          },
          {
            service: getProfessionalLegalServicesColumnTitle(19),
            price: 'od 3900,00-8000,00',
          },
          {
            service: getProfessionalLegalServicesColumnTitle(20),
            price: '9000,00 / 2500,00 za udział',
          },
        ]"
      ></PricesTable>
    </PageContainer>
    <div class="gradient-background mt-20">
      <PageContainer class="pb-10">
        <h2 class="<md:px-2">{{ t("rules.title") }}</h2>
        <div
          class="<md:grid <md:grid-cols-1 <md:gap-0 flex justify-center gap-12 text-white/75"
        >
          <ul class="grid grid-cols-1 gap-2">
            <li>
              {{ t("rules.items[0]") }}
            </li>
            <li v-html="t('rules.items[1]')"></li>
            <li>
              {{ t("rules.items[2]") }}
            </li>
          </ul>
          <img class="<md:hidden" src="/assets/svg/men-shake-hands.svg" />
          <ul class="grid grid-cols-1 gap-2">
            <li>
              {{ t("rules.items[3]") }}
            </li>
            <li>
              {{ t("rules.items[4]") }}
            </li>
            <li>
              {{ t("rules.items[5]") }}
            </li>
          </ul>
        </div>
      </PageContainer>
    </div>
    <PageContainer class="mt-20 mb-10 <md:px-2">
      <ContactForm></ContactForm>
    </PageContainer>
  </section>
</template>
<style lang="scss" scoped>
.gradient-background {
  width: 100%;
  background: rgb(255, 96, 90);
  background: linear-gradient(
    90deg,
    rgba(255, 96, 90, 1) 0%,
    rgba(255, 204, 73, 1) 100%
  );
  color: var(--color-light);
}
</style>
